import { Marked } from 'marked'
import DOMPurify from 'dompurify'

export interface TocEntry {
  id: string
  text: string
  depth: number
}

/** `foo bar/baz!` -> `h-foo-bar-baz`. The `h-` prefix is load-bearing, not
 *  decorative: DOMPurify's DOM-clobbering guard silently strips an `id` that
 *  matches a reserved global name (`id="title"` on a heading called
 *  "Title" loses its id with no warning, since it could otherwise shadow
 *  `document.title` once the sanitised HTML is in a real document). That
 *  turned every TOC link built from the bare slug into a click that goes
 *  nowhere -- found by testing this function's own output, not guessed.
 *  A guide heading will never collide with a reserved global once it can
 *  never BE a bare reserved word. */
function slugifyHeading(text: string): string {
  const slug = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
  return `h-${slug || 'section'}`
}

/** Guide images are written relative to `frontend/public/guides/<slug>/...`
 *  (`guides/<slug>/file.png`, no leading slash, matching the folder name).
 *  Vite serves `public/` from the site root, so the browser needs the
 *  leading slash; an already-absolute or external URL is left untouched. */
function resolveImageHref(href: string): string {
  if (/^(https?:)?\/\//.test(href) || href.startsWith('/')) {
    return href
  }
  return `/${href}`
}

/**
 * Renders one guide's markdown BODY (front matter already stripped) to
 * sanitised HTML, plus the table of contents that fell out of it.
 *
 * Two deliberate choices this function makes and nowhere else does:
 *  - Headings get a stable slug `id` so `/guides/<slug>#heading-id` deep
 *    links and the in-page TOC both resolve to the same element.
 *  - Images are rendered as ordinary `<img>` tags; there is no build-time
 *    check here for whether the file exists (Guides.tsx's own effect
 *    handles a missing file at render time, by design -- see its own doc
 *    comment for why a build-time gate was dropped).
 */
export function renderGuideMarkdown(body: string): { html: string; toc: TocEntry[] } {
  const toc: TocEntry[] = []
  const seen = new Map<string, number>()
  const marked = new Marked({
    renderer: {
      heading(token) {
        let id = slugifyHeading(token.text)
        const count = seen.get(id) ?? 0
        seen.set(id, count + 1)
        if (count > 0) {
          id = `${id}-${count}`
        }
        toc.push({ id, text: token.text, depth: token.depth })
        return `<h${token.depth} id="${id}">${this.parser.parseInline(token.tokens)}</h${token.depth}>\n`
      },
      image(token) {
        const href = resolveImageHref(token.href)
        const alt = token.text ?? ''
        return `<img src="${href}" alt="${alt}" loading="lazy">`
      },
    },
  })
  const rawHtml = marked.parse(body, { async: false }) as string
  const html = DOMPurify.sanitize(rawHtml)
  return { html, toc }
}
